import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LoaderService } from '../../shared/services/loader.service';
import { ApiService } from '../../shared/services/api.service';
import { NotificationService } from '../../shared/services/notification.service';
import { ModalService } from '../../shared/services/modal.service';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { BreadcrumComponent } from '../breadcrum/breadcrum.component';
import { applyImageFallback } from '../../shared/utils/image-fallback';

interface BlogItem {
  id: number;
  title: string;
  description: string;
  author_name: string;
  author_description: string;
  created_date: string;
  status: string;
  images: string[];
  image_type: string[];
  video_url?: string;
  duration?: string;
}

@Component({
  selector: 'app-blog-stories-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, BreadcrumComponent],
  templateUrl: './blog-stories-list.component.html',
  styleUrls: ['./blog-stories-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlogStoriesListComponent implements OnInit, OnDestroy {
  allItems: BlogItem[] = [];
  filterType: 'all' | 'IMAGE BASED' | 'video' = 'all';
  searchQuery = '';
  isSearchActive = false;
  sub: Subscription = new Subscription();
  breadcrumbData: any[] = [];

  // Pagination properties
  currentPage = 1;
  limit = 10;
  offset = 0;
  totalResults = 0;
  totalPages = 1;
  isClientSidePagination = false;

  // Video modal properties
  isVideoModalOpen = false;
  isVideoPlaying = true;
  showIndicator = false;
  private indicatorTimer?: any;
  currentVideoUrl: SafeResourceUrl | null = null;
  currentVideoTitle = '';
  currentOriginalVideoUrl = '';
  isMobile = false;

  private destroy$ = new Subject<void>();
  private messageListener?: (event: MessageEvent) => void;

  @ViewChild('sortDropdown', { static: false }) sortDropdown!: ElementRef;
  @ViewChild('sortButton', { static: false }) sortButton!: ElementRef;

  constructor(
    private loaderService: LoaderService,
    private apiService: ApiService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
    private modalService: ModalService,
  ) { }

  ngOnInit() {
    this.detectMobile();
    this.getBloglist();
    this.scrollToTop();
    this.initializeBreadcrumb();

    if (typeof window !== 'undefined') {
      this.messageListener = (event: MessageEvent) => {
        try {
          const data =
            typeof event.data === 'string'
              ? JSON.parse(event.data)
              : event.data;
          if (data?.event === 'onStateChange') {
            if (data.info === 1) {
              this.isVideoPlaying = true;
              this.cdr.markForCheck();
            } else if (data.info === 2 || data.info === 0) {
              this.isVideoPlaying = false;
              this.cdr.markForCheck();
            }
          }
        } catch (_) {}
      };
      window.addEventListener('message', this.messageListener);
    }
  }

  detectMobile(): void {
    if (typeof window !== 'undefined') {
      this.isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        ) || window.innerWidth < 768;
    }
  }

  // ngOnDestroy(): void {
  //   // Clean up all subscriptions
  //   this.destroy$.next();
  //   this.destroy$.complete();
  //   this.sub.unsubscribe();
  // }

  scrollToTop(): void {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  get filteredItems(): BlogItem[] {
    if (this.allItems.length > this.limit) {
      const start = (this.currentPage - 1) * this.limit;
      return this.allItems.slice(start, start + this.limit);
    }
    return this.allItems;
  }

  filterTypeChange(tab: 'all' | 'IMAGE BASED' | 'video'): void {
    this.filterType = tab;
    this.currentPage = 1;
    this.offset = 0;
    this.isClientSidePagination = false;
    this.allItems = [];
    this.totalResults = 0;
    this.totalPages = 1;
    this.getBloglist(tab);
  }

  handlePageChange(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.offset = (page - 1) * this.limit;
    this.scrollToTop();

    if (this.isClientSidePagination || this.allItems.length > this.limit) {
      this.cdr.markForCheck();
    } else {
      this.getBloglist(this.filterType);
    }
  }

  onSearch(): void {
    if (this.isValidSearchQuery()) {
      this.isSearchActive = true;
      this.currentPage = 1;
      this.offset = 0;
      this.isClientSidePagination = false;
      this.allItems = [];
      this.totalResults = 0;
      this.totalPages = 1;
      this.notificationService.info(`Searching for "${this.searchQuery}"...`);
      this.getBloglist(this.filterType);
    } else {
      this.notificationService.error(
        'Please enter at least 3 characters to search',
      );
    }
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.isSearchActive = false;
    this.currentPage = 1;
    this.offset = 0;
    this.isClientSidePagination = false;
    this.allItems = [];
    this.totalResults = 0;
    this.totalPages = 1;
    this.getBloglist(this.filterType);
  }

  isValidSearchQuery(): boolean {
    if (!this.searchQuery) return false;
    // Count alphanumeric characters
    const alphanumericCount = (this.searchQuery.match(/[a-zA-Z0-9]/g) || [])
      .length;
    return alphanumericCount >= 3;
  }

  get canSearch(): boolean {
    return this.isValidSearchQuery() && !this.isSearchActive;
  }

  getBloglist(tab?: 'all' | 'IMAGE BASED' | 'video'): void {
    let sortBy: string = '';
    if (this.selectedSort === 'newToOld') {
      sortBy = 'newest_to_oldest';
    } else if (this.selectedSort === 'oldToNew') {
      sortBy = 'oldest_to_newest';
    } else {
      sortBy = '';
    }
    let screenType = 'Desktop';

    if (typeof window !== 'undefined') {
      if (window.innerWidth < 640) {
        screenType = 'Mobile';
      } else if (window.innerWidth < 1024) {
        screenType = 'Tablet';
      }
    }

    const payload: any = {
      action_type: '',
      offset: this.offset,
      limit: this.limit,
      search_text: this.searchQuery,
      sort_by: sortBy,
      image_type: '',
    };
    if (tab == 'IMAGE BASED') {
      payload.action_type = 'Get Blogs';
    } else if (tab == 'video') {
      payload.action_type = 'Get Blogs Video';
    } else {
      payload.action_type = 'Get All Blogs';
    }

    this.loaderService.show();

    // Clear previous results before API call to prevent showing stale data
    this.allItems = [];

    this.apiService
      .getBlogs(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res && res.success) {
            let rawList: BlogItem[] = [];
            if (Array.isArray(res.data)) {
              rawList = res.data;
            } else if (Array.isArray(res?.data?.blogs)) {
              rawList = res.data.blogs;
            } else if (Array.isArray(res?.data?.blog_list)) {
              rawList = res.data.blog_list;
            } else if (Array.isArray(res?.data?.items)) {
              rawList = res.data.items;
            } else if (Array.isArray(res?.data?.list)) {
              rawList = res.data.list;
            } else if (Array.isArray(res?.blogs)) {
              rawList = res.blogs;
            } else if (Array.isArray(res?.blog_list)) {
              rawList = res.blog_list;
            }

            const possibleTotal =
              res?.total_result ??
              res?.totalResult ??
              res?.total_count ??
              res?.totalCount ??
              res?.count ??
              res?.total ??
              res?.data?.total_result ??
              res?.data?.totalResult ??
              res?.data?.total_count ??
              res?.data?.totalCount ??
              res?.data?.count ??
              res?.data?.total;

            const parsedTotal =
              possibleTotal !== undefined && possibleTotal !== null
                ? Number(possibleTotal)
                : NaN;

            const limit = Number(this.limit) || 10;

            if (rawList.length > limit) {
              // Server returned more items than limit - client-side pagination mode
              this.isClientSidePagination = true;
              this.allItems = rawList;
              this.totalResults =
                !isNaN(parsedTotal) && parsedTotal > 0
                  ? parsedTotal
                  : rawList.length;
              this.totalPages = Math.max(
                1,
                Math.ceil(this.totalResults / limit),
              );
            } else {
              // Server returned <= limit items
              this.isClientSidePagination = false;
              this.allItems = rawList;

              if (!isNaN(parsedTotal) && parsedTotal > 0) {
                this.totalResults = parsedTotal;
                this.totalPages = Math.max(
                  1,
                  Math.ceil(this.totalResults / limit),
                );
              } else if (this.offset === 0 && rawList.length < limit) {
                this.totalResults = rawList.length;
                this.totalPages = 1;
              } else if (rawList.length === limit) {
                this.totalPages = Math.max(
                  this.currentPage + 1,
                  this.totalPages,
                );
                this.totalResults = this.totalPages * limit;
              } else {
                this.totalPages = Math.max(1, this.currentPage);
                this.totalResults = this.offset + rawList.length;
              }
            }

            if (this.currentPage > this.totalPages) {
              this.currentPage = 1;
              this.offset = 0;
            }

            if (this.allItems.length === 0 && this.isSearchActive) {
              this.notificationService.info(
                `No results found for "${this.searchQuery}"`,
              );
            }

            console.log('Blog results:', {
              itemsCount: this.allItems.length,
              displayedCount: this.filteredItems.length,
              totalResults: this.totalResults,
              totalPages: this.totalPages,
              currentPage: this.currentPage,
              isClientSide: this.isClientSidePagination,
            });

            this.cdr.markForCheck();
          } else {
            this.allItems = [];
            this.totalResults = 0;
            this.totalPages = 1;
            this.isClientSidePagination = false;
            this.cdr.markForCheck();
          }
          this.loaderService.hide();
        },
        error: (err) => {
          this.allItems = [];
          this.totalResults = 0;
          this.totalPages = 1;
          this.currentPage = 1;
          this.offset = 0;
          this.isClientSidePagination = false;
          console.error('Failed to fetch blog list', err);
          this.loaderService.hide();
          this.cdr.markForCheck();
        },
      });
  }

  isSortDropdownOpen = false;
  selectedSort = '';

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isSortDropdownOpen) {
      const clickedInside =
        this.sortDropdown?.nativeElement?.contains(event.target as Node) ||
        this.sortButton?.nativeElement?.contains(event.target as Node);

      if (!clickedInside) {
        this.isSortDropdownOpen = false;
      }
    }
  }

  toggleSortDropdown() {
    this.isSortDropdownOpen = !this.isSortDropdownOpen;
    this.cdr.markForCheck();
  }

  getSortDisplayText(): string {
    switch (this.selectedSort) {
      case 'popular':
        return 'Popular';
      case 'oldToNew':
        return 'Old to New';
      case 'newToOld':
        return 'New to Old';
      default:
        return 'Sort By';
    }
  }

  selectSortOption(option: string) {
    this.selectedSort = option;
    this.isSortDropdownOpen = false;
    // Reset pagination when sort changes
    this.currentPage = 1;
    this.offset = 0;
    this.isClientSidePagination = false;
    this.allItems = [];
    this.totalResults = 0;
    this.totalPages = 1;

    const sortText = this.getSortDisplayText();

    // Refresh the blog list with the new sort option
    this.getBloglist(this.filterType);
  }

  initializeBreadcrumb(): void {
    this.breadcrumbData = [{ label: 'Blogs & Videos', active: true }];
  }

  onBreadcrumbClick(event: { label: string; url: any; index: number }): void {
    console.log('Breadcrumb clicked:', event);
  }

  // Video modal methods
  // openVideoModal(videoUrl: string, title: string): void {
  //   this.currentOriginalVideoUrl = videoUrl;
  //   this.currentVideoTitle = title;

  //   const embedUrl = this.convertToEmbedUrl(videoUrl);

  //   if (!embedUrl) {
  //     return;
  //   }

  //   this.currentVideoUrl =
  //     this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
  //   this.isVideoModalOpen = true;
  //   this.cdr.markForCheck();
  //   console.log('Opening video modal:', {
  //     videoUrl,
  //     embedUrl,
  //     title,
  //     isMobile: this.isMobile,
  //   });
  // }

  // closeVideoModal(): void {
  //   this.isVideoModalOpen = false;
  //   this.currentVideoUrl = null;
  //   this.currentVideoTitle = '';
  //   this.currentOriginalVideoUrl = '';
  //   this.cdr.markForCheck();
  // }

  // Helper to extract video url from any property returned by API
  getVideoUrl(item: any): string {
    return item?.video_url || item?.video_path || item?.youtube_url || '';
  }

  hasImage(item: BlogItem): boolean {
    return item?.images && Array.isArray(item.images) && item.images.length > 0;
  }

  hasVideo(item: any): boolean {
    const url = this.getVideoUrl(item);
    return !!url && url.trim() !== '';
  }

  // Universal extractor for YouTube video ID (supports watch?v=, shorts/, embed/, youtu.be, m.youtube.com, etc.)
  extractYouTubeVideoId(url: string | undefined): string | null {
    if (!url) {
      return null;
    }
    const cleanUrl = url.trim();
    const regExp = /(?:youtube(?:-nocookie)?\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?|shorts)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = cleanUrl.match(regExp);
    return match ? match[1] : null;
  }

  convertToEmbedUrl(url: string): string {
    if (!url) {
      console.error('Video URL is empty');
      return '';
    }

    const videoId = this.extractYouTubeVideoId(url);
    if (videoId) {
      const origin =
        typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : '';
      const params = new URLSearchParams({
        autoplay: '1',
        controls: '1',
        playsinline: '1',
        rel: '0',
        modestbranding: '1',
        enablejsapi: '1',
        fs: '1',
      });
      if (origin) {
        params.set('origin', origin);
      }
      return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
    }

    console.error('Could not extract video ID from URL:', url);
    return url;
  }

  getYouTubeThumbnailUrl(videoUrl: string): string | null {
    const videoId = this.extractYouTubeVideoId(videoUrl);
    if (!videoId) {
      return null;
    }
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  }

  getYouTubeThumbnailUrlWithFallback(
    videoUrl: string | undefined,
  ): string | null {
    if (!videoUrl) {
      return null;
    }

    const videoId = this.extractYouTubeVideoId(videoUrl);
    if (!videoId) {
      return null;
    }

    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }

  navigateToBlogDetail(item: BlogItem): void {
    const id = item?.id ?? (item as any)?.blog_id ?? (item as any)?.row_id ?? (item as any)?._id;
    if (!this.hasVideo(item) && id) {
      this.router.navigate(['/blog-detail', id], { state: { blogItem: item } });
    }
  }

  handleImageClick(item: BlogItem, event: Event): void {
    const videoUrl = this.getVideoUrl(item);
    if (this.hasVideo(item) && videoUrl) {
      event.stopPropagation();
      this.openVideoModal(videoUrl, item.title);
    } else {
      this.navigateToBlogDetail(item);
    }
  }

  handleCardClick(item: BlogItem, event: Event): void {
    const videoUrl = this.getVideoUrl(item);
    if (this.hasVideo(item) && videoUrl) {
      this.openVideoModal(videoUrl, item.title);
    } else {
      this.navigateToBlogDetail(item);
    }
  }

  isBlog(item: BlogItem): boolean {
    return !this.hasVideo(item) && this.hasImage(item);
  }

  onThumbnailError(event: Event): void {
    const img = event.target as HTMLImageElement;
    const currentSrc = img.src;

    if (currentSrc.includes('maxresdefault')) {
      const videoId = currentSrc.match(/vi\/([^\/]+)/)?.[1];
      if (videoId) {
        img.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
        this.cdr.markForCheck();
        return;
      }
    }

    applyImageFallback(event);
    this.cdr.markForCheck();
  }


  openVideoModal(videoUrl: string, title: string): void {
    const embedUrl = this.convertToEmbedUrl(videoUrl);
    if (!embedUrl) {
      return;
    }

    this.currentOriginalVideoUrl = videoUrl;
    this.currentVideoTitle = title;
    this.currentVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
    this.isVideoModalOpen = true;
    this.isVideoPlaying = true;
    this.lockBodyScroll();
    this.cdr.markForCheck();
  }

  showFeedbackIndicator(): void {
    this.showIndicator = true;
    if (this.indicatorTimer) {
      clearTimeout(this.indicatorTimer);
    }
    this.indicatorTimer = setTimeout(() => {
      this.showIndicator = false;
      this.cdr.markForCheck();
    }, 600);
  }

  togglePlayPause(): void {
    const iframe = this.document?.getElementById(
      'youtubeVideoIframe',
    ) as HTMLIFrameElement;
    if (!iframe?.contentWindow) {
      this.isVideoPlaying = !this.isVideoPlaying;
      this.showFeedbackIndicator();
      return;
    }

    const command = this.isVideoPlaying ? 'pauseVideo' : 'playVideo';
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: 'command', func: command, args: '' }),
      '*',
    );
    this.isVideoPlaying = !this.isVideoPlaying;
    this.showFeedbackIndicator();
    this.cdr.markForCheck();
  }

  closeVideoModal(): void {
    if (this.indicatorTimer) {
      clearTimeout(this.indicatorTimer);
    }
    this.showIndicator = false;
    const iframe = this.document?.getElementById(
      'youtubeVideoIframe',
    ) as HTMLIFrameElement;
    if (iframe?.contentWindow) {
      try {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }),
          '*',
        );
      } catch (_) {}
    }
    this.isVideoModalOpen = false;
    this.currentVideoUrl = null; // destroys the iframe so audio stops
    this.currentVideoTitle = '';
    this.currentOriginalVideoUrl = '';
    this.isVideoPlaying = false;
    this.unlockBodyScroll();
    this.cdr.markForCheck();
  }

  // Plain CSS lock only. No touch listeners, so iframe taps are never intercepted.
  private lockBodyScroll(): void {
    this.modalService.openModal('blog-story-video', () => this.closeVideoModal());
  }

  private unlockBodyScroll(): void {
    this.modalService.closeModal('blog-story-video');
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isVideoModalOpen) {
      this.closeVideoModal();
    }
  }

  ngOnDestroy(): void {
    if (this.indicatorTimer) {
      clearTimeout(this.indicatorTimer);
    }
    if (this.messageListener && typeof window !== 'undefined') {
      window.removeEventListener('message', this.messageListener);
    }
    this.unlockBodyScroll();
    this.destroy$.next();
    this.destroy$.complete();
    this.sub.unsubscribe();
  }
}
